// components/RightPanel.js
import React, { useCallback } from 'react';
import StickiesViewRight from './StickiesViewRight';
import ThreadsView from './ThreadsView';
import NoteViewMain from './MainPanel/NoteViewMain';
import { usePanelContext, PANEL_COMPONENTS } from './contexts/PanelContext';
import { useTheme } from './contexts/ThemeContext';
import ImageGallery from './RightPanel/ImageGallery';
import styles from './RightPanel.module.css';
import { useStickies } from './contexts/StickiesContext';
import { useNotes } from './contexts/NotesContext';
import FormatBar from './FormatBar';

const RightPanel = ({ 
  isVisible, 
  selectedNoteId, 
  categories,
  galleryRefreshTrigger,
}) => {
  const { rightPanelContent, isSwapped } = usePanelContext();
  const { theme } = useTheme();
  const { stickies, updateSticky, removeSticky, addSticky, saveStickyLayout } = useStickies();
  const { 
    getSelectedNote, 
    updateNoteContent, 
    updateNoteTitle, 
    updateNoteCategory,
    refreshNoteImages,
    getNoteImages
  } = useNotes();

  // Use same handlers as NoteEditorContainer
  const handleNoteChange = useCallback((content) => {
    updateNoteContent(selectedNoteId, content);
  }, [selectedNoteId, updateNoteContent]);

  const handleTitleChange = useCallback((title) => {
    updateNoteTitle(selectedNoteId, title);
  }, [selectedNoteId, updateNoteTitle]);

  const handleCategoryChange = useCallback((categoryId) => {
    updateNoteCategory(selectedNoteId, categoryId);
  }, [selectedNoteId, updateNoteCategory]);

  const renderContent = () => {
    const { rightPanelContent, isSwapped, PANEL_COMPONENTS } = usePanelContext();
    
    const stickyProps = {
      selectedNoteId,
      isInRightPanel: true
    };

    const currentNote = getSelectedNote();
  
    // Updated to use consistent handlers for both panels
    const noteViewProps = {
      noteId: selectedNoteId,
      note: currentNote,
      categories: categories,
      isImageUploadExpanded: false,
      isGalleryExpanded: false,
      onNoteChange: handleNoteChange,
      onTitleChange: handleTitleChange,
      onCategoryChange: handleCategoryChange,
      onImagePaste: async () => {
        await refreshNoteImages(selectedNoteId);
      },
      images: getNoteImages(selectedNoteId) || [],
      isInRightPanel: true
    };
    
    // Special handling for swapped state
    if (isSwapped) {
      // When swapped and note view is requested in right panel
      if (rightPanelContent === PANEL_COMPONENTS.RIGHT_PANEL.NOTE_VIEW) {
        return <NoteViewMain {...noteViewProps} />;
      }
    }
    
    switch (rightPanelContent) {
      case PANEL_COMPONENTS.RIGHT_PANEL.NOTE_VIEW:
        return <NoteViewMain {...noteViewProps} />;
      case PANEL_COMPONENTS.RIGHT_PANEL.STICKY_NOTES:
        return <StickiesViewRight {...stickyProps} />;
      case PANEL_COMPONENTS.RIGHT_PANEL.THREADS:
        return <ThreadsView />;
      case PANEL_COMPONENTS.RIGHT_PANEL.IMAGE_GALLERY:
        return <ImageGallery 
          selectedNoteId={selectedNoteId} 
          refreshTrigger={galleryRefreshTrigger} 
        />;
      default:
        return <StickiesViewRight {...stickyProps} />;
    }
  };

  // Determine if we should show the FormatBar based on content type
  const shouldShowFormatBar = () => {
    const { rightPanelContent, isSwapped, PANEL_COMPONENTS } = usePanelContext();
    
    // Show FormatBar when Note View is active in right panel
    if (rightPanelContent === PANEL_COMPONENTS.RIGHT_PANEL.NOTE_VIEW) {
      return true;
    }
    
    // Also show when panels are swapped and main note view is in right panel
    if (isSwapped && rightPanelContent === PANEL_COMPONENTS.RIGHT_PANEL.NOTE_VIEW) {
      return true;
    }
    
    return false;
  };

  return (
    <div
      className={`${styles.rightPanel} ${isVisible ? styles.open : styles.closed}`}
      role="complementary"
      aria-hidden={!isVisible}
      data-theme={theme.name}
    >
      {shouldShowFormatBar() && (
        <div className={styles.rightPanelFormatBarContainer}>
          <FormatBar />
        </div>
      )}
      
      <div className={styles.rightPanelContent}>
        {renderContent()}
      </div>
    </div>
  );
};

export default React.memo(RightPanel);